// iter239 (2026-02) — Referral + Card paywall modals.
// Due componenti stacked:
//   • CardQuotaReachedModal: paywall post-3-card con 2 CTA (VIP / Invita)
//   • InviteFriendsModal: mostra codice amico + progress + share
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { X, Crown, Users, Share2, MessageCircle } from 'lucide-react';
import { trackShare } from '../lib/referralShare';
import { vipFeatureText } from '../lib/vipLimits';
import { inviteCodeSource } from '../lib/inviteCode';

const API = '/api';

// ====================================================================
// CardQuotaReachedModal — paywall quando l'utente base ha visto 3 card
// ====================================================================
export const CardQuotaReachedModal = ({ open, onClose, onOpenInvite, description, inviteSubtitle, vipSource, testId = 'card-quota-reached-modal' }) => {
  const navigate = useNavigate();

  if (!open) return null;

  const openVipChat = () => {
    onClose?.();
    if (vipSource) { navigate(`/vip?da=${vipSource}`); return; }
    navigate('/chat', {
      state: {
        prefilledMessage: 'Voglio accedere alle analisi VIP',
      },
    });
  };

  return (
    <div className="fixed inset-0 z-[90] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" onClick={onClose} data-testid={testId}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-[#111] border border-[#F5A623]/20 rounded-3xl w-full max-w-md max-h-[90vh] overflow-y-auto"
      >
        <div className="relative p-6">
          <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-white" data-testid="card-quota-close-btn">
            <X className="w-5 h-5" />
          </button>

          <div className="text-center space-y-3 mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-[#F5A623]/20 to-[#FF6B6B]/10 border border-[#F5A623]/30 mx-auto">
              <Crown className="w-8 h-8 text-[#F5A623]" />
            </div>
            <h2 className="text-xl font-black text-white" style={{ fontFamily: 'Barlow Condensed' }}>
              HAI RAGGIUNTO IL LIMITE GIORNALIERO
            </h2>
            <p className="text-sm text-gray-400 leading-relaxed">
              {description || (vipSource ? vipFeatureText(0) : <>Hai consultato le <strong className="text-white">3 card cartellino/marcatore</strong> giornaliere gratuite.
              Per ogni amico che si registra con il tuo codice ricevi <strong className="text-emerald-400">7 giorni</strong> di <strong className="text-white">marcatori, cartellini e Multipla IA</strong> 👇</>)}
            </p>
          </div>

          <div className="space-y-3">
            <button
              onClick={openVipChat}
              className="w-full p-4 rounded-2xl bg-gradient-to-r from-[#F5A623] to-[#FF6B6B] text-white font-black flex items-center gap-3 group"
              data-testid="card-quota-vip-btn"
            >
              <Crown className="w-5 h-5 flex-shrink-0" />
              <div className="text-left flex-1">
                <div className="text-sm" style={{ fontFamily: 'Barlow Condensed', letterSpacing: 1 }}>{vipSource ? 'SCOPRI IL VIP' : 'DIVENTA VIP'}</div>
              </div>
            </button>

            <button
              onClick={() => { onClose?.(); onOpenInvite?.(); }}
              className="w-full p-4 rounded-2xl bg-[#1A1A1A] border border-emerald-500/30 text-white font-black flex items-center gap-3 hover:border-emerald-500"
              data-testid="card-quota-invite-btn"
            >
              <Users className="w-5 h-5 flex-shrink-0 text-emerald-400" />
              <div className="text-left flex-1">
                <div className="text-sm" style={{ fontFamily: 'Barlow Condensed', letterSpacing: 1 }}>INVITA AMICI</div>
                <div className="text-[10px] font-medium text-gray-400">{inviteSubtitle || '7 giorni gratis per ogni amico: marcatori, cartellini e Multipla IA'}</div>
              </div>
            </button>
          </div>

          <p className="text-[10px] text-gray-500 text-center mt-5 leading-relaxed">
            Il limite si resetta ogni giorno a mezzanotte. Diventa VIP o invita amici per sbloccarli.
          </p>
        </div>
      </div>
    </div>
  );
};

// ====================================================================
// InviteFriendsModal — mostra codice amico + progress + share
// ====================================================================
export const InviteFriendsModal = ({ open, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const shareBtnRef = React.useRef(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    axios.post(`${API}/referral/invite-window-open`).catch(() => {});
    axios.get(`${API}/referral/me`)
      .then((r) => setData(r.data))
      .catch((e) => {
        toast.error('Impossibile caricare il tuo codice amico');
      })
      .finally(() => setLoading(false));
  }, [open]);

  // iter239 fix (2026-02) — focus automatico sul bottone CONDIVIDI dopo
  // il caricamento. L'azione principale è condividere, non copiare.
  useEffect(() => {
    if (!open || !data || loading) return;
    const t = setTimeout(() => shareBtnRef.current?.focus(), 150);
    return () => clearTimeout(t);
  }, [open, data, loading]);

  if (!open) return null;

  const shareNative = async () => {
    if (!data?.share_text) return;
    trackShare('invite');
    try {
      const { Capacitor } = await import('@capacitor/core');
      if (Capacitor.isNativePlatform()) {
        const { Share } = await import('@capacitor/share');
        await Share.share({ text: data.share_text, dialogTitle: 'Invita amici' });
        return;
      }
      if (navigator.share) {
        await navigator.share({ text: data.share_text });
        return;
      }
      await navigator.clipboard.writeText(data.share_text);
      toast.success('Messaggio di invito copiato: incollalo dove vuoi!');
    } catch { /* user cancelled */ }
  };

  const count = data?.referrals_count || 0;
  const unlocked = data?.is_card_unlocked;
  const daysRemaining = data?.days_remaining;

  return (
    <div className="fixed inset-0 z-[91] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" onClick={onClose} data-testid="invite-friends-modal">
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-[#111] border border-emerald-500/20 rounded-3xl w-full max-w-md max-h-[90vh] overflow-y-auto"
      >
        <div className="relative p-6">
          <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-white" data-testid="invite-close-btn">
            <X className="w-5 h-5" />
          </button>

          <div className="text-center space-y-2 mb-5">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-emerald-500/20 to-emerald-500/5 border border-emerald-500/30 mx-auto">
              <Users className="w-7 h-7 text-emerald-400" />
            </div>
            <h2 className="text-xl font-black text-white" style={{ fontFamily: 'Barlow Condensed' }}>
              INVITA AMICI
            </h2>
            <p className="text-xs text-gray-400 leading-relaxed">
              Per ogni amico che si registra con il tuo codice ricevete <strong className="text-emerald-400">7 giorni gratis a testa</strong> di marcatori, cartellini e Multipla IA. I giorni si sommano.
            </p>
          </div>

          {loading && (
            <div className="py-8 text-center text-gray-500 text-xs">Caricamento...</div>
          )}

          {data && (
            <>
              {/* Status */}
              <div className="grid grid-cols-2 gap-2 mb-4">
                <div className="bg-[#0F1419] border border-gray-800 rounded-2xl p-3 text-center">
                  <p className="text-2xl font-black text-white" data-testid="referral-progress">{count}</p>
                  <p className="text-[10px] text-gray-400 uppercase tracking-wider">Amici registrati</p>
                </div>
                <div className={`rounded-2xl p-3 text-center border ${unlocked ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-[#0F1419] border-gray-800'}`}>
                  <p className={`text-2xl font-black ${unlocked ? 'text-emerald-400' : 'text-gray-500'}`} data-testid="referral-days-remaining">{unlocked ? daysRemaining : 0}</p>
                  <p className="text-[10px] text-gray-400 uppercase tracking-wider">Giorni attivi</p>
                </div>
              </div>

              <div className="bg-[#0F1419] rounded-xl p-3 mb-4 border border-emerald-500/20 text-center">
                <p className="text-[10px] text-gray-400 uppercase tracking-wider">Il tuo codice amico</p>
                <p className="text-2xl font-black text-emerald-400 tracking-widest" style={{ fontFamily: 'Barlow Condensed' }} data-testid="referral-code">{data.code}</p>
              </div>

              {/* Istruzioni */}
              <div className="bg-[#0F1419] rounded-xl p-3 mb-4 border border-gray-800">
                <p className="text-[11px] text-gray-400 leading-relaxed">
                  📲 Tocca <strong className="text-white">Invita amici</strong> e scegli WhatsApp, Telegram, SMS o l&apos;app che preferisci. Il messaggio contiene il tuo link e il tuo codice: <strong className="text-white">ogni amico</strong> che si registra con il tuo codice regala <strong className="text-white">7 giorni</strong> a te e 7 giorni a lui, appena conferma l&apos;email e torna nell&apos;app un altro giorno.
                </p>
              </div>

              {/* Share buttons */}
              <div className="space-y-2">
                <button
                  ref={shareBtnRef}
                  onClick={shareNative}
                  className="w-full py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-black text-sm flex items-center justify-center gap-2 ring-2 ring-emerald-400/50 ring-offset-2 ring-offset-[#111] focus:outline-none focus:ring-emerald-300"
                  data-testid="referral-share-btn"
                >
                  <Share2 className="w-4 h-4" /> INVITA AMICI
                </button>
              </div>

              {/* Spiegazione extra */}
              <p className="text-[10px] text-gray-600 text-center mt-4 leading-relaxed">
                Valgono solo amici veri: niente premio con email usa e getta o se l&apos;amico usa il tuo stesso telefono. Massimo 3 amici premiati al giorno e 10 al mese.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ====================================================================
// GoogleReferralCodeModal — popup post-Google-login per codice amico
// ====================================================================
export const GoogleReferralCodeModal = ({ open, onClose }) => {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const submit = async () => {
    const c = code.trim().toUpperCase();
    if (c.length !== 4) {
      toast.error('Codice non valido (4 caratteri richiesti)');
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/referral/apply-code`, { code: c, source: inviteCodeSource(c) });
      toast.success(r.data.message || 'Codice applicato!');
      // mark as applied so we don't show again
      try { localStorage.setItem('referralCodeApplied', '1'); } catch {}
      onClose?.();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Errore';
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  const skip = () => {
    try { localStorage.setItem('referralCodeApplied', '1'); } catch {}
    onClose?.();
  };

  return (
    <div className="fixed inset-0 z-[92] bg-black/85 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" data-testid="google-referral-modal">
      <div className="bg-[#111] border border-emerald-500/20 rounded-3xl w-full max-w-md p-6 space-y-4">
        <div className="text-center space-y-2">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-500/10 border border-emerald-500/30 mx-auto">
            <Users className="w-7 h-7 text-emerald-400" />
          </div>
          <h2 className="text-xl font-black text-white" style={{ fontFamily: 'Barlow Condensed' }}>
            HAI UN CODICE AMICO?
          </h2>
          <p className="text-xs text-gray-400 leading-relaxed">
            Inserisci il codice di 4 caratteri che un amico ti ha condiviso: riceverete entrambi 7 giorni di marcatori, cartellini e Multipla IA quando confermi l'email e torni nell'app un altro giorno.
          </p>
        </div>

        <input
          type="text"
          maxLength={4}
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase().slice(0, 4))}
          placeholder="KR45"
          className="w-full px-4 py-3 rounded-xl bg-[#1A1A1A] border border-emerald-500/30 text-white text-center text-3xl font-black tracking-widest focus:outline-none focus:border-emerald-400"
          style={{ fontFamily: 'Barlow Condensed' }}
          data-testid="google-referral-input"
        />

        <div className="flex gap-2">
          <button
            onClick={skip}
            className="flex-1 py-3 rounded-xl bg-[#1A1A1A] border border-gray-700 text-gray-400 text-xs font-bold"
            data-testid="google-referral-skip-btn"
          >
            SALTA
          </button>
          <button
            onClick={submit}
            disabled={busy || code.trim().length !== 4}
            className={`flex-1 py-3 rounded-xl font-black text-xs ${busy || code.trim().length !== 4 ? 'bg-gray-700 text-gray-500' : 'bg-emerald-500 text-white hover:bg-emerald-600'}`}
            data-testid="google-referral-apply-btn"
          >
            {busy ? '...' : 'APPLICA'}
          </button>
        </div>
      </div>
    </div>
  );
};
